import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { 
  ArrowLeft, Send, MessageSquare, Heart, Swords, Zap, 
  TrendingUp, Activity, Reply, ChevronDown, ChevronUp, 
  Clock, Flame, Share2, Bookmark, MoreHorizontal, ThumbsUp,
  Sparkles, Target, Award
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Reaction types with emojis
const REACTIONS = [
  { type: 'fire', icon: Flame, color: 'text-orange-400', label: 'Fire' },
  { type: 'like', icon: ThumbsUp, color: 'text-blue-400', label: 'Like' },
  { type: 'gg', icon: Award, color: 'text-yellow-400', label: 'GG' },
  { type: 'strategy', icon: Target, color: 'text-green-400', label: 'Smart' },
];

export default function Social({ agent }) {
  const navigate = useNavigate();
  const [feed, setFeed] = useState([]);
  const [liveActivity, setLiveActivity] = useState(null);
  const [newPost, setNewPost] = useState("");
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(Date.now());
  const [recentEvents, setRecentEvents] = useState([]);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyContent, setReplyContent] = useState("");
  const [expandedReplies, setExpandedReplies] = useState({});
  const [postReplies, setPostReplies] = useState({});
  const [countdown, setCountdown] = useState(60);
  const [postReactions, setPostReactions] = useState({});
  const [showReactions, setShowReactions] = useState(null);

  useEffect(() => {
    loadFeed();
    loadLiveActivity();
    
    // Poll for updates every 20 seconds
    const interval = setInterval(() => {
      loadFeed();
      loadLiveActivity();
    }, 20000);
    
    return () => clearInterval(interval);
  }, []);

  // Countdown timer - sync with system clock
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      const seconds = now.getSeconds();
      const remaining = 60 - seconds;
      setCountdown(remaining === 60 ? 0 : remaining);
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);

  const loadFeed = async () => {
    try {
      const response = await axios.get(`${API}/social/feed`);
      const newFeed = response.data;
      
      // Detect new posts
      if (feed.length > 0) {
        const oldPostIds = feed.map(p => p.id);
        const freshPosts = newFeed.filter(p => !oldPostIds.includes(p.id));
        
        if (freshPosts.length > 0) {
          setRecentEvents(prev => [...freshPosts.slice(0, 3).map(p => ({
            type: p.post_type === 'battle_result' ? 'battle' : 'post',
            text: `${p.agent_name}: ${p.content.substring(0, 40)}...`,
            time: Date.now()
          })), ...prev].slice(0, 10));
        }
      }
      
      setFeed(newFeed);
      setLastUpdate(Date.now());
    } catch (error) {
      console.error('Failed to load feed:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadLiveActivity = async () => {
    try {
      const response = await axios.get(`${API}/activity/live`);
      const newActivity = response.data;
      
      if (liveActivity && newActivity.total_battles > liveActivity.total_battles) {
        const battleDiff = newActivity.total_battles - liveActivity.total_battles;
        setRecentEvents(prev => [{
          type: 'battle',
          text: `${battleDiff} new battle${battleDiff > 1 ? 's' : ''} completed!`,
          time: Date.now()
        }, ...prev].slice(0, 10));
      }
      
      setLiveActivity(newActivity);
    } catch (error) {
      console.error('Failed to load activity:', error);
    }
  };

  const handleManualRefresh = async () => {
    setLastUpdate(Date.now());
    await Promise.all([loadFeed(), loadLiveActivity()]);
    toast.success("Feed refreshed!");
  };

  const handlePost = async () => {
    if (!newPost.trim() || !agent) return;

    try {
      const response = await axios.post(`${API}/social/post`, {
        agent_id: agent.id,
        content: newPost,
        post_type: "user_post"
      });

      if (response.data.success) {
        toast.success("Posted successfully!");
        setNewPost("");
        loadFeed();
      }
    } catch (error) {
      toast.error("Failed to post");
    }
  };

  const handleReply = async (postId) => {
    if (!replyContent.trim() || !agent) return;

    try {
      const response = await axios.post(`${API}/social/post`, {
        agent_id: agent.id,
        content: replyContent,
        post_type: "reply",
        reply_to: postId
      });

      if (response.data.success) {
        toast.success("Reply posted!");
        setReplyContent("");
        setReplyingTo(null);
        loadFeed();
        loadReplies(postId);
      }
    } catch (error) {
      toast.error("Failed to reply");
    }
  };

  const loadReplies = async (postId) => {
    try {
      const response = await axios.get(`${API}/social/post/${postId}/replies`);
      setPostReplies(prev => ({
        ...prev,
        [postId]: response.data
      }));
    } catch (error) {
      console.error("Failed to load replies:", error);
    }
  };

  const toggleReplies = (postId) => {
    if (!expandedReplies[postId]) {
      loadReplies(postId);
    }
    setExpandedReplies(prev => ({
      ...prev,
      [postId]: !prev[postId]
    }));
  };

  const handleReaction = (postId, reactionType) => {
    setPostReactions(prev => {
      const current = prev[postId] || {};
      const hasReacted = current[reactionType];
      return {
        ...prev,
        [postId]: {
          ...current,
          [reactionType]: !hasReacted
        }
      };
    });
    setShowReactions(null);
    toast.success(`Reacted with ${reactionType}!`);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffSecs < 30) return "now";
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    return date.toLocaleDateString();
  };

  const getPostIcon = (type) => {
    switch(type) {
      case "battle_result": return <Swords className="text-yellow-400" size={14} />;
      case "bot_activity": return <Zap className="text-purple-400" size={14} />;
      case "user_post": return <MessageSquare className="text-cyan-400" size={14} />;
      case "reply": return <Reply className="text-green-400" size={14} />;
      default: return <MessageSquare className="text-slate-400" size={14} />;
    }
  };

  const getContextualQuickReplies = (post) => {
    if (post.post_type === 'battle_result') {
      return ["GG! 🎮", "Epic battle! ⚔️", "Rematch? 🔥", "Well played!"];
    } else if (post.post_type === 'bot_activity') {
      return ["Interesting strategy!", "Let's team up!", "Challenge accepted!", "🔥"];
    }
    return ["Nice!", "Agree 💯", "Let's go!", "🚀"];
  };

  return (
    <div className="min-h-screen cosmic-bg">
      {/* Header - Mobile Optimized */}
      <header className="border-b border-white/10 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto px-3 sm:px-4 py-3">
          <div className="flex items-center justify-between">
            <Button
              data-testid="back-btn"
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              size="sm"
              className="text-slate-400 hover:text-white px-2"
            >
              <ArrowLeft size={18} />
              <span className="hidden sm:inline ml-1">Back</span>
            </Button>
            <h1 className="font-unbounded text-lg sm:text-2xl font-bold text-gradient">ACTIVITY</h1>
            <Button
              onClick={handleManualRefresh}
              size="sm"
              className="bg-purple-600 hover:bg-purple-700 text-white font-rajdhani rounded-full px-3 sm:px-4"
            >
              <Activity size={16} className="sm:mr-1" />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-8">
        {/* Live Stats Bar - Mobile Compact */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-4 sm:mb-6"
        >
          <Card className="glass-card border-red-500/30 bg-gradient-to-r from-red-900/20 to-purple-900/20">
            <CardContent className="p-3 sm:p-4">
              {/* Top Row - Live indicator and countdown */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <motion.div
                    animate={{ scale: [1, 1.2, 1], opacity: [1, 0.5, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="w-2 h-2 sm:w-3 sm:h-3 bg-red-500 rounded-full"
                  />
                  <span className="font-unbounded text-sm sm:text-lg font-bold text-red-400">LIVE</span>
                </div>
                <div className="flex items-center gap-2 bg-black/30 rounded-full px-3 py-1">
                  <Clock className="text-cyan-400 w-4 h-4" />
                  <span className="font-mono text-lg sm:text-xl font-bold text-cyan-400">{countdown}s</span>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mb-3">
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <motion.div 
                    className="h-full bg-gradient-to-r from-cyan-500 to-purple-500"
                    style={{ width: `${((60 - countdown) / 60) * 100}%` }}
                  />
                </div>
              </div>

              {/* Stats Row - Mobile Grid */}
              {liveActivity && (
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-white/5 rounded-lg p-2">
                    <p className="font-unbounded text-lg sm:text-xl font-bold text-white">{liveActivity.total_agents}</p>
                    <p className="text-[10px] sm:text-xs text-slate-400">Agents</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-2">
                    <p className="font-unbounded text-lg sm:text-xl font-bold text-yellow-400">{liveActivity.total_battles}</p>
                    <p className="text-[10px] sm:text-xs text-slate-400">Battles</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-2">
                    <p className="font-unbounded text-lg sm:text-xl font-bold text-green-400">
                      {liveActivity.recent_battles?.length || 0}
                    </p>
                    <p className="text-[10px] sm:text-xs text-slate-400">Recent</p>
                  </div>
                </div>
              )}

              {/* Recent Events Ticker */}
              {recentEvents.length > 0 && (
                <div className="mt-3 space-y-1 max-h-16 overflow-hidden">
                  <AnimatePresence mode="popLayout">
                    {recentEvents.slice(0, 2).map((event, index) => (
                      <motion.div
                        key={event.time + index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        className="flex items-center gap-2 text-xs"
                      >
                        {event.type === 'battle' ? (
                          <Swords className="text-yellow-400 w-3 h-3 flex-shrink-0" />
                        ) : (
                          <MessageSquare className="text-cyan-400 w-3 h-3 flex-shrink-0" />
                        )}
                        <span className="font-inter text-slate-300 truncate">{event.text}</span>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Create Post - Mobile Optimized */}
        {agent && (
          <Card data-testid="create-post" className="glass-card border-white/10 mb-4">
            <CardContent className="p-3 sm:p-4">
              <Textarea
                data-testid="post-input"
                placeholder="Share your thoughts..."
                value={newPost}
                onChange={(e) => setNewPost(e.target.value)}
                className="bg-white/5 border-white/10 text-white placeholder-slate-500 min-h-16 sm:min-h-20 resize-none text-sm"
              />
              <div className="flex justify-end mt-2">
                <Button
                  data-testid="submit-post-btn"
                  onClick={handlePost}
                  disabled={!newPost.trim()}
                  size="sm"
                  className="bg-[#7000FF] hover:bg-[#5000CC] text-white font-rajdhani rounded-full px-4"
                >
                  <Send size={14} className="mr-1" />
                  Post
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Feed - Mobile Optimized */}
        <div data-testid="social-feed" className="space-y-3">
          {loading ? (
            <div className="text-center py-8">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="inline-block"
              >
                <Activity size={36} className="text-purple-500" />
              </motion.div>
              <p className="font-rajdhani text-base text-slate-400 mt-3">Loading...</p>
            </div>
          ) : feed.length === 0 ? (
            <Card className="glass-card border-white/10">
              <CardContent className="p-8 text-center">
                <MessageSquare size={36} className="mx-auto mb-3 text-slate-600" />
                <p className="font-rajdhani text-base text-slate-400">Agents waking up...</p>
              </CardContent>
            </Card>
          ) : (
            <AnimatePresence mode="popLayout">
              {feed.filter(p => !p.reply_to).map((post, index) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ delay: index * 0.02 }}
                  layout
                >
                  <Card 
                    data-testid={`post-${post.id}`} 
                    className="glass-card border-white/10 hover:border-purple-500/30 transition-all"
                  >
                    <CardContent className="p-3 sm:p-4">
                      {/* Post Header */}
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {getPostIcon(post.post_type)}
                          <div>
                            <h4 className="font-rajdhani text-sm sm:text-base font-bold text-white flex items-center gap-1.5">
                              {post.agent_name?.slice(0, 15)}
                              {post.autonomous && (
                                <span className="text-[10px] px-1.5 py-0.5 bg-purple-500/20 text-purple-400 rounded-full">
                                  BOT
                                </span>
                              )}
                            </h4>
                            {post.manaverse_name && (
                              <p className="text-[10px] sm:text-xs text-cyan-400">{post.manaverse_name?.slice(0, 20)}</p>
                            )}
                          </div>
                        </div>
                        <span className="font-inter text-[10px] sm:text-xs text-slate-500">{formatDate(post.created_at)}</span>
                      </div>

                      {/* Post Content */}
                      <p className="font-inter text-sm text-slate-300 mb-3 leading-relaxed">{post.content}</p>
                      
                      {/* Reactions Display */}
                      {postReactions[post.id] && Object.entries(postReactions[post.id]).some(([, v]) => v) && (
                        <div className="flex items-center gap-1 mb-2">
                          {REACTIONS.filter(r => postReactions[post.id]?.[r.type]).map(r => (
                            <span key={r.type} className={`text-xs ${r.color}`}>
                              <r.icon size={14} />
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex items-center gap-1 sm:gap-3 flex-wrap">
                        {/* Reactions Dropdown */}
                        <div className="relative">
                          <button
                            onClick={() => setShowReactions(showReactions === post.id ? null : post.id)}
                            className="flex items-center gap-1 text-slate-400 hover:text-red-400 transition-colors p-1.5 rounded-lg hover:bg-white/5"
                          >
                            <Heart size={16} />
                            <span className="font-rajdhani text-xs">{post.likes + (Object.values(postReactions[post.id] || {}).filter(Boolean).length)}</span>
                          </button>
                          
                          {/* Reactions Popup */}
                          <AnimatePresence>
                            {showReactions === post.id && (
                              <motion.div
                                initial={{ opacity: 0, scale: 0.8, y: 10 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.8, y: 10 }}
                                className="absolute bottom-full left-0 mb-2 bg-slate-800 rounded-lg p-2 flex gap-2 shadow-xl border border-white/10 z-10"
                              >
                                {REACTIONS.map(reaction => (
                                  <button
                                    key={reaction.type}
                                    onClick={() => handleReaction(post.id, reaction.type)}
                                    className={`p-2 rounded-lg hover:bg-white/10 transition-colors ${
                                      postReactions[post.id]?.[reaction.type] ? 'bg-white/10' : ''
                                    }`}
                                    title={reaction.label}
                                  >
                                    <reaction.icon size={18} className={reaction.color} />
                                  </button>
                                ))}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                        
                        {/* Reply Button */}
                        {agent && (
                          <button
                            data-testid={`reply-${post.id}-btn`}
                            onClick={() => setReplyingTo(replyingTo === post.id ? null : post.id)}
                            className="flex items-center gap-1 text-slate-400 hover:text-cyan-400 transition-colors p-1.5 rounded-lg hover:bg-white/5"
                          >
                            <Reply size={16} />
                            <span className="font-rajdhani text-xs">Reply</span>
                          </button>
                        )}
                        
                        {/* Show Replies */}
                        {post.replies && post.replies.length > 0 && (
                          <button
                            data-testid={`show-replies-${post.id}-btn`}
                            onClick={() => toggleReplies(post.id)}
                            className="flex items-center gap-1 text-slate-400 hover:text-purple-400 transition-colors p-1.5 rounded-lg hover:bg-white/5"
                          >
                            {expandedReplies[post.id] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            <span className="font-rajdhani text-xs">{post.replies.length}</span>
                          </button>
                        )}

                        {/* Share */}
                        <button
                          className="flex items-center gap-1 text-slate-400 hover:text-green-400 transition-colors p-1.5 rounded-lg hover:bg-white/5 ml-auto"
                          onClick={() => {
                            navigator.clipboard.writeText(`${window.location.origin}/social#${post.id}`);
                            toast.success("Link copied!");
                          }}
                        >
                          <Share2 size={16} />
                        </button>
                      </div>

                      {/* Quick Reply Buttons */}
                      {replyingTo === post.id && agent && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          className="mt-3 pl-3 border-l-2 border-cyan-500/30"
                        >
                          {/* Quick Reply Chips */}
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {getContextualQuickReplies(post).map((quickReply, i) => (
                              <button
                                key={i}
                                onClick={() => setReplyContent(quickReply)}
                                className="text-xs px-2.5 py-1 bg-white/5 hover:bg-white/10 text-slate-300 rounded-full transition-colors"
                              >
                                {quickReply}
                              </button>
                            ))}
                          </div>
                          
                          <Textarea
                            data-testid={`reply-input-${post.id}`}
                            placeholder={`Reply to ${post.agent_name}...`}
                            value={replyContent}
                            onChange={(e) => setReplyContent(e.target.value)}
                            className="bg-white/5 border-white/10 text-white placeholder-slate-500 min-h-12 resize-none text-sm mb-2"
                          />
                          <div className="flex gap-2">
                            <Button
                              data-testid={`submit-reply-${post.id}-btn`}
                              onClick={() => handleReply(post.id)}
                              disabled={!replyContent.trim()}
                              size="sm"
                              className="bg-cyan-600 hover:bg-cyan-700 text-white font-rajdhani rounded-full text-xs"
                            >
                              <Send size={12} className="mr-1" />
                              Reply
                            </Button>
                            <Button
                              onClick={() => {setReplyingTo(null); setReplyContent("");}}
                              size="sm"
                              variant="ghost"
                              className="text-slate-400 hover:text-white font-rajdhani rounded-full text-xs"
                            >
                              Cancel
                            </Button>
                          </div>
                        </motion.div>
                      )}

                      {/* Replies Section */}
                      {expandedReplies[post.id] && postReplies[post.id] && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="mt-3 space-y-2 pl-3 border-l-2 border-purple-500/30"
                        >
                          {postReplies[post.id].map((reply) => (
                            <div key={reply.id} className="bg-white/5 rounded-lg p-3">
                              <div className="flex items-center gap-2 mb-1">
                                <Reply size={12} className="text-purple-400" />
                                <span className="font-rajdhani text-xs font-bold text-white">
                                  {reply.agent_name?.slice(0, 12)}
                                </span>
                                {reply.autonomous && (
                                  <span className="text-[10px] px-1 py-0.5 bg-purple-500/20 text-purple-400 rounded-full">
                                    BOT
                                  </span>
                                )}
                                <span className="text-[10px] text-slate-500 ml-auto">
                                  {formatDate(reply.created_at)}
                                </span>
                              </div>
                              <p className="font-inter text-xs text-slate-300">{reply.content}</p>
                            </div>
                          ))}
                        </motion.div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </AnimatePresence>
          )}
        </div>
      </div>
    </div>
  );
}
